import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  createAction,
} from "@reduxjs/toolkit";
import chatService from "../../services/chatService";
import toast from "react-hot-toast";
import { extractErrorMessage } from "utils/ErrorHandlers";
import { RootState } from "store/Store";
import {
  ChatRequest,
  ChatJobStatus,
  ChatJobStatusResponse,
  ChatHistoryRequest,
  ChatHistoryResponse,
  ChatState,
  ChatMessage,
  MessageRole,
  SessionDeactivationResponse,
  SessionDeactivationRequest,
  WebSocketStatusEvent,
  WebSocketResponseEvent,
  WebSocketErrorEvent,
  WebSocketStreamChunkEvent,
  WebSocketStreamDoneEvent,
  WebSocketStreamErrorEvent,
} from "../../Interfaces/interface.chat";
import chatWebSocketService from "../../services/chatWebSocketService";
import { config } from "process";
import { PenToolIcon, PowerOff } from "lucide-react";

const getInitialStreamingPreference = (): boolean => {
  if (typeof window === "undefined") {
    return true;
  }
  const stored = window.localStorage.getItem("streamingEnabled");
  if (stored === null) return true;
  return stored !== "false";
};

interface JobStartedPayload {
  jobId: string;
  status: ChatJobStatus;
  estimatedWaitTime?: string | null;
}

export const chatJobStarted =
  createAction<JobStartedPayload>("chat/jobStarted");
export const chatJobStatusUpdated = createAction<ChatJobStatusResponse>(
  "chat/jobStatusUpdated",
);
export const chatJobCleared = createAction("chat/jobCleared");
export const chatJobErrored = createAction<string>("chat/jobErrored");

// WebSocket event actions
export const chatWebSocketStatus = createAction<WebSocketStatusEvent>(
  "chat/webSocketStatus",
);
export const chatWebSocketResponse = createAction<WebSocketResponseEvent>(
  "chat/webSocketResponse",
);
export const chatWebSocketError = createAction<WebSocketErrorEvent>(
  "chat/webSocketError",
);
export const chatWebSocketStreamChunk = createAction<WebSocketStreamChunkEvent>(
  "chat/webSocketStreamChunk",
);
export const chatWebSocketStreamDone = createAction<WebSocketStreamDoneEvent>(
  "chat/webSocketStreamDone",
);
export const chatWebSocketStreamError = createAction<WebSocketStreamErrorEvent>(
  "chat/webSocketStreamError",
);

const initialState: ChatState = {
  currentSessionId: null,
  messages: [],
  history: null,
  loading: false,
  error: null,
  jobId: null,
  jobStatus: null,
  queuePosition: null,
  lastUpdatedAt: null,
  estimatedWaitTime: null,
  jobError: null,
  streamingEnabled: getInitialStreamingPreference(),
  isStreaming: false,
  streamingMessageId: null,
  streamingContent: "",
  progressNode: null,
  progressMessage: null,
};

export const sendMessage = createAsyncThunk<
  void,
  ChatRequest,
  { rejectValue: string; state: RootState }
>(
  "chat/sendMessage",
  async (request, { rejectWithValue, dispatch, getState }) => {
    try {
      // Check if WebSocket is connected
      if (!chatWebSocketService.isConnected()) {
        console.error(
          "Slice Connection Check Failed - isConnected:",
          chatWebSocketService.isConnected(),
        );
        throw new Error(
          "WebSocket is not connected. Please wait a moment and try again.",
        );
      }

      // Get device fingerprint and streaming preference
      const deviceFingerprint =
        localStorage.getItem("deviceFingerprint") || undefined;
      const streamingEnabled = getState().aiChat.streamingEnabled;

      // Send message via WebSocket
      const success = chatWebSocketService.sendMessage({
        user_id: request.userId,
        message: request.message,
        session_id: request.sessionId,
        device_fingerprint: deviceFingerprint,
        stream: streamingEnabled,
      });

      if (!success) {
        throw new Error("Failed to send message via WebSocket");
      }

      // The response will be handled via WebSocket events
      // No need to return anything, the response will come through WebSocket
    } catch (error: any) {
      console.log("error: ", error);
      const message = extractErrorMessage(error);
      dispatch(chatJobErrored(message));
      toast.error(message);
      return rejectWithValue(message);
    }
  },
);

export const fetchChatHistory = createAsyncThunk<
  ChatHistoryResponse,
  ChatHistoryRequest,
  { rejectValue: string }
>("chat/fetchChatHistory", async (params, { rejectWithValue }) => {
  try {
    return await chatService.getChatHistory(params);
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    return rejectWithValue(message);
  }
});

export const deactivateSession = createAsyncThunk<
  SessionDeactivationResponse,
  SessionDeactivationRequest,
  { rejectValue: string }
>("chat/deactivateSession", async (request, { rejectWithValue }) => {
  try {
    console.log("request: ", request);
    const response = await chatService.deactivateSession(request);
    sessionStorage.removeItem(`chatSessionId_${request.userId}`);
    return response;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    return rejectWithValue(message);
  }
});

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    addUserMessage: (
      state,
      action: PayloadAction<{ message: string; timestamp?: string }>,
    ) => {
      const { message, timestamp = new Date().toISOString() } = action.payload;
      state.messages.push({
        id: Date.now().toString(),
        role: MessageRole.USER,
        content: message,
        timestamp,
        metadata: null,
      });
    },
    setCurrentSession: (
      state,
      action: PayloadAction<{ sessionId: string; messages?: ChatMessage[] }>,
    ) => {
      const { sessionId, messages = [] } = action.payload;
      state.currentSessionId = sessionId;
      state.messages = messages;
      if (state.history?.userId) {
        sessionStorage.setItem(
          `chatSessionId_${state.history.userId}`,
          sessionId,
        );
      }
    },
    clearChat: (state) => {
      if (state.history?.userId) {
        sessionStorage.removeItem(`chatSessionId_${state.history.userId}`);
      }
      state.currentSessionId = null;
      state.messages = [];
      state.history = null;
      state.loading = false;
      state.error = null;
      state.jobId = null;
      state.jobStatus = null;
      state.queuePosition = null;
      state.lastUpdatedAt = null;
      state.estimatedWaitTime = null;
      state.jobError = null;
      state.isStreaming = false;
      state.streamingMessageId = null;
      state.streamingContent = "";
      state.progressNode = null;
      state.progressMessage = null;
    },
    setStreamingEnabledPreference: (state, action: PayloadAction<boolean>) => {
      state.streamingEnabled = action.payload;
      if (typeof window !== "undefined") {
        window.localStorage.setItem(
          "streamingEnabled",
          action.payload ? "true" : "false",
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.jobError = null;
        state.progressNode = null;
        state.progressMessage = null;
        state.streamingMessageId = null;
        state.streamingContent = "";
        state.isStreaming = state.streamingEnabled;
      })
      .addCase(sendMessage.fulfilled, (state) => {
        // Message sent successfully via WebSocket
        // The actual response will be handled by chatWebSocketResponse action
        // Keep loading true until we receive the WebSocket response
        // state.loading remains true - will be set to false by chatWebSocketResponse
      })
      .addCase(
        sendMessage.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Unknown error";
          state.isStreaming = false;
          state.streamingMessageId = null;
          state.streamingContent = "";
          state.progressNode = null;
          state.progressMessage = null;
        },
      )
      .addCase(fetchChatHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchChatHistory.fulfilled,
        (
          state,
          action: PayloadAction<
            ChatHistoryResponse,
            string,
            { arg: ChatHistoryRequest }
          >,
        ) => {
          state.loading = false;
          state.history = action.payload;
          if (action.meta.arg.sessionId && action.payload.sessions.length > 0) {
            state.currentSessionId = action.meta.arg.sessionId;
            state.messages =
              action.payload.sessions.find(
                (session) => session.sessionId === action.meta.arg.sessionId,
              )?.messages || [];
          }
        },
      )
      .addCase(
        fetchChatHistory.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Unknown error";
        },
      )
      .addCase(deactivateSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deactivateSession.fulfilled,
        (state, action: PayloadAction<SessionDeactivationResponse>) => {
          state.loading = false;
          state.currentSessionId = null;
          state.messages = [];
          state.history = null;
        },
      )
      .addCase(
        deactivateSession.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Unknown error";
        },
      )
      .addCase(chatJobStarted, (state, action) => {
        state.jobId = action.payload.jobId;
        state.jobStatus = action.payload.status;
        state.queuePosition = null;
        state.lastUpdatedAt = new Date().toISOString();
        state.estimatedWaitTime = action.payload.estimatedWaitTime || null;
        state.jobError = null;
      })
      .addCase(chatJobStatusUpdated, (state, action) => {
        state.jobId = action.payload.jobId;
        state.jobStatus = action.payload.status;
        state.queuePosition =
          action.payload.queuePosition ?? state.queuePosition;
        state.lastUpdatedAt = action.payload.lastUpdatedAt || null;
        if (
          action.payload.status === "failed" ||
          action.payload.status === "not_found"
        ) {
          state.jobError =
            action.payload.error ||
            (action.payload.status === "not_found"
              ? "Chat job expired. Please try again."
              : "Chat job failed.");
        }
      })
      .addCase(chatJobErrored, (state, action) => {
        state.jobError = action.payload;
      })
      .addCase(chatJobCleared, (state) => {
        state.jobId = null;
        state.jobStatus = null;
        state.queuePosition = null;
        state.lastUpdatedAt = null;
        state.estimatedWaitTime = null;
        state.jobError = null;
        state.progressNode = null;
        state.progressMessage = null;
        state.isStreaming = false;
        state.streamingMessageId = null;
        state.streamingContent = "";
      })
      .addCase(chatWebSocketStatus, (state, action) => {
        if (action.payload.job_id) {
          state.jobId = action.payload.job_id;
        }
        const statusData = action.payload.data;
        if (typeof statusData === "string") {
          state.jobStatus = statusData as ChatJobStatus;
          state.progressMessage = null;
        } else if (statusData && typeof statusData === "object") {
          state.progressMessage = statusData.message || null;
        } else {
          state.progressMessage = null;
        }
        state.progressNode =
          action.payload.node !== undefined ? action.payload.node : null;
        state.lastUpdatedAt = action.payload.timestamp;
        // Status events indicate work in progress unless explicitly completed
        state.loading = true;
        state.jobError = null;
      })
      .addCase(chatWebSocketStreamChunk, (state, action) => {
        const payload = (action.payload as any).data || action.payload;
        const { message_id, content } = payload;

        if (!message_id) return;

        let baseContent =
          state.streamingMessageId === message_id ? state.streamingContent : "";

        const updatedContent = `${baseContent}${content || ""}`;

        state.streamingMessageId = message_id;
        state.streamingContent = updatedContent;
        state.isStreaming = true;
        state.loading = true;
      })
      .addCase(chatWebSocketStreamDone, (state, action) => {
        const payload = (action.payload as any).data || action.payload;
        let metadata = (action.payload as any).metadata || action.payload;
        let {
          message_id,
          full_content,
          text,
          completed_at,
          timestamp,
          suggestions,
          products,
        } = payload;

        if (!message_id) return;

        // Extract session_id also from metadata if specific top-level one is missing
        const finalSessionId = (metadata?.session_id as string) || null;

        const existingMessage = state.messages.find(
          (message) => message.id === message_id,
        );

        let finalContent =
          text ||
          full_content ||
          existingMessage?.content ||
          state.streamingContent ||
          "";

        try {
          if (
            typeof finalContent === "string" &&
            (finalContent.trim().startsWith("{") ||
              finalContent.trim().startsWith("["))
          ) {
            const parsed = JSON.parse(finalContent);
            if (parsed && typeof parsed === "object") {
              finalContent =
                parsed.text || parsed.content || parsed.message || finalContent;

              // If parsed content has products, use them
              if (parsed.products) {
                products = parsed.products;
              }
              // If parsed content has suggestions or actions, use them
              if (parsed.suggestions || parsed.actions) {
                suggestions = parsed.suggestions || parsed.actions;
              }
              // If parsed content has metadata, merge it
              if (parsed.metadata) {
                metadata = { ...metadata, ...parsed.metadata };
              }
            }
          }
        } catch (e) {
          // Ignore parsing error, treat as plain text
        }

        // Use completed_at or timestamp
        const finalTimestamp =
          completed_at || timestamp || new Date().toISOString();

        if (existingMessage) {
          existingMessage.content = finalContent;
          existingMessage.timestamp = finalTimestamp;
          existingMessage.metadata = metadata ?? existingMessage.metadata;
          existingMessage.suggestions =
            suggestions ?? existingMessage.suggestions;
          existingMessage.products = products ?? existingMessage.products;
        } else {
          state.messages.push({
            id: message_id,
            role: MessageRole.ASSISTANT,
            content: finalContent,
            timestamp: finalTimestamp,
            metadata: metadata || null,
            suggestions,
            products,
          });
        }

        state.loading = false;
        state.isStreaming = false;
        state.streamingMessageId = null;
        state.streamingContent = "";
        state.jobStatus = "completed";
        state.jobId = null;
        state.queuePosition = null;
        state.lastUpdatedAt = finalTimestamp;
        state.estimatedWaitTime = null;
        state.jobError = null;
        state.progressNode = null;
        state.progressMessage = null;
        if (finalSessionId) {
          state.currentSessionId = finalSessionId;
        }

        if (finalSessionId) {
          const userId = state.history?.userId;
          if (userId) {
            sessionStorage.setItem(`chatSessionId_${userId}`, finalSessionId);
          }
        }
      })
      .addCase(chatWebSocketStreamError, (state, action) => {
        const payload = (action.payload as any).data || action.payload;
        const errorMessage = payload.error;
        state.loading = false;
        state.isStreaming = false;
        state.streamingMessageId = null;
        state.streamingContent = "";
        state.jobError = errorMessage;
        state.error = errorMessage;
        state.progressNode = null;
        state.progressMessage = null;
        toast.error(errorMessage);
      })
      .addCase(chatWebSocketResponse, (state, action) => {
        const { data } = action.payload;
        state.loading = false;
        state.jobStatus = "completed";
        state.currentSessionId = data.session_id;

        // Parse response (could be JSON string or plain text)
        let responseContent = data.response;
        let parsedResponse: any = null;

        try {
          parsedResponse = JSON.parse(responseContent);
          // If it's a JSON object, extract the text content
          if (parsedResponse && typeof parsedResponse === "object") {
            responseContent =
              parsedResponse.text || parsedResponse.content || responseContent;
          }
        } catch {
          // Not JSON, use as-is
        }

        state.messages.push({
          id: data.messageId || Date.now().toString(),
          role: MessageRole.ASSISTANT,
          content: responseContent,
          timestamp: data.completed_at,
          metadata: data.metadata || null,
          suggestions: data.suggestions,
          products: data.products,
        });

        // Store session ID
        if (data.session_id) {
          const userId = state.history?.userId;
          if (userId) {
            sessionStorage.setItem(`chatSessionId_${userId}`, data.session_id);
          }
        }

        // Clear job state
        state.jobId = null;
        state.jobStatus = null;
        state.queuePosition = null;
        state.lastUpdatedAt = null;
        state.estimatedWaitTime = null;
        state.jobError = null;
        state.isStreaming = false;
        state.streamingMessageId = null;
        state.streamingContent = "";
        state.progressNode = null;
        state.progressMessage = null;
      })
      .addCase(chatWebSocketError, (state, action) => {
        state.loading = false;
        state.jobError = action.payload.data;
        state.error = action.payload.data;
        state.isStreaming = false;
        state.streamingMessageId = null;
        state.streamingContent = "";
        state.progressNode = null;
        state.progressMessage = null;
        toast.error(action.payload.data);
      });
  },
});

export const {
  addUserMessage,
  setCurrentSession,
  clearChat,
  setStreamingEnabledPreference,
} = chatSlice.actions;

export const selectCurrentSessionId = (state: RootState) =>
  state.aiChat.currentSessionId;
export const selectMessages = (state: RootState) => state.aiChat.messages;
export const selectHistory = (state: RootState) => state.aiChat.history;
export const selectChatLoading = (state: RootState) => state.aiChat.loading;
export const selectChatError = (state: RootState) => state.aiChat.error;
export const selectChatJobStatus = (state: RootState) => state.aiChat.jobStatus;
export const selectChatJobId = (state: RootState) => state.aiChat.jobId;
export const selectChatQueuePosition = (state: RootState) =>
  state.aiChat.queuePosition;
export const selectChatEstimatedWaitTime = (state: RootState) =>
  state.aiChat.estimatedWaitTime;
export const selectChatJobError = (state: RootState) => state.aiChat.jobError;
export const selectChatStreamingEnabled = (state: RootState) =>
  state.aiChat.streamingEnabled;
export const selectChatIsStreaming = (state: RootState) =>
  state.aiChat.isStreaming;
export const selectChatProgressNode = (state: RootState) =>
  state.aiChat.progressNode;
export const selectChatProgressMessage = (state: RootState) =>
  state.aiChat.progressMessage;

export default chatSlice.reducer;
